import { useEffect } from 'react';
import { type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Checkbox, Col, Form, Row, theme, Typography } from 'antd';
import {
  daysOfWeekOptions,
  getDaysObject,
} from 'pos-client/components/locations/utils/daysOptionsHelpers.ts';
import { createFormFields } from 'pos-client/components/shared/Form/FormUtils.ts';
import { PosFormItem } from 'pos-client/components/shared/Form/PosFormItem.tsx';
import {
  PosTimePicker,
  type PosTimePickerProps,
} from 'pos-client/components/shared/Form/PosTimePicker.tsx';
import { fullWidth } from 'pos-client/utils/cssHelpers.ts';
import type { RouteAvailabilityFormModel } from 'pos-shared/models/routeSchema';

const CheckboxGroup = Checkbox.Group;

interface RouteAvailabilityProps {
  form: UseFormReturn<RouteAvailabilityFormModel>;
}
const AvailabilityTabContent = ({ form }: RouteAvailabilityProps) => {
  const { t } = useTranslation();
  const { Text } = Typography;
  const { token } = theme.useToken();

  // Watch individual fields to ensure component updates
  const startTimeWindowStart = form.watch('startTimeWindow.startTime');
  const startTimeWindowEnd = form.watch('startTimeWindow.endTime');
  const finishTimeWindowStart = form.watch('finishTimeWindow.startTime');
  const finishTimeWindowEnd = form.watch('finishTimeWindow.endTime');
  const serviceTimeWindowStart = form.watch('serviceTimeWindow.startTime');
  const serviceTimeWindowEnd = form.watch('serviceTimeWindow.endTime');

  // Trigger validation whenever any time picker value changes
  useEffect(() => {
    form.trigger();
  }, [
    startTimeWindowStart,
    startTimeWindowEnd,
    finishTimeWindowStart,
    finishTimeWindowEnd,
    serviceTimeWindowStart,
    serviceTimeWindowEnd,
    form,
  ]);

  const defaultTimePickerProps: Partial<PosTimePickerProps> = {
    use12Hours: true,
    showNow: false,
    needConfirm: true,
    style: fullWidth,
    allowClear: false,
  };

  return (
    <Form fields={createFormFields(form)} layout="vertical">
      <Row>
        <PosFormItem
          control={form.control}
          name="daysOfTheWeek"
          label={t('routes.serviceDays')}
          required
        >
          <CheckboxGroup
            options={daysOfWeekOptions}
            onChange={(val) => getDaysObject(val as string[])}
          />
        </PosFormItem>
      </Row>
      <Row gutter={token.marginXXL} style={{ marginBottom: token.marginSM }}>
        <Col span={24} style={{ marginBottom: token.marginXS }}>
          <Text strong>{t('routes.startTimes')}</Text>
        </Col>
        <Col span={12}>
          <PosTimePicker
            control={form.control}
            name="startTimeWindow.startTime"
            label={t('scheduling.availability.earliestStartTime')}
            required
            {...defaultTimePickerProps}
          />
        </Col>
        <Col span={12}>
          <PosTimePicker
            control={form.control}
            name="startTimeWindow.endTime"
            label={t('scheduling.availability.latestStartTime')}
            required
            {...defaultTimePickerProps}
          />
        </Col>
      </Row>
      <Row gutter={token.marginXXL} style={{ marginBottom: token.marginSM }}>
        <Col span={24} style={{ marginBottom: token.marginXS }}>
          <Text strong>{t('routes.endTimes')}</Text>
        </Col>
        <Col span={12}>
          <PosTimePicker
            control={form.control}
            name="finishTimeWindow.startTime"
            label={t('scheduling.availability.earliestEndTime')}
            required
            {...defaultTimePickerProps}
          />
        </Col>
        <Col span={12}>
          <PosTimePicker
            control={form.control}
            name="finishTimeWindow.endTime"
            label={t('scheduling.availability.latestEndTime')}
            required
            {...defaultTimePickerProps}
          />
        </Col>
      </Row>
      <Row gutter={token.marginXXL}>
        <Col span={24} style={{ marginBottom: token.marginXS }}>
          <Text strong>{t('routes.serviceTimes')}</Text>
        </Col>
        <Col span={12}>
          <PosTimePicker
            control={form.control}
            name="serviceTimeWindow.startTime"
            label={t('scheduling.availability.earliestServiceTime')}
            required
            {...defaultTimePickerProps}
          />
        </Col>
        <Col span={12}>
          <PosTimePicker
            control={form.control}
            name="serviceTimeWindow.endTime"
            label={t('scheduling.availability.latestServiceTime')}
            required
            {...defaultTimePickerProps}
          />
        </Col>
      </Row>
    </Form>
  );
};

export default AvailabilityTabContent;
